"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface Segment {
  text: string;
  className?: string;
}

interface TypewriterTextProps {
  segments: Segment[];
  className?: string;
  cursorClassName?: string;
  speed?: number;
  delay?: number;
}

export function TypewriterText({
  segments,
  className,
  cursorClassName,
  speed = 50,
  delay = 0,
}: TypewriterTextProps) {
  const [displayedSegments, setDisplayedSegments] = useState<Segment[]>([]);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setStarted(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [delay]);

  useEffect(() => {
    if (!started) return;

    if (currentSegmentIndex >= segments.length) return;

    const interval = setInterval(() => {
      const currentSegment = segments[currentSegmentIndex];

      if (currentCharIndex < currentSegment.text.length) {
        setDisplayedSegments((prev) => {
          const newSegments = [...prev];
          if (!newSegments[currentSegmentIndex]) {
            newSegments[currentSegmentIndex] = { ...currentSegment, text: "" };
          }
          newSegments[currentSegmentIndex].text = currentSegment.text.slice(
            0,
            currentCharIndex + 1,
          );
          return newSegments;
        });
        setCurrentCharIndex((prev) => prev + 1);
      } else {
        setCurrentSegmentIndex((prev) => prev + 1);
        setCurrentCharIndex(0);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [segments, speed, started, currentSegmentIndex, currentCharIndex]);

  return (
    <span className={cn("inline-block", className)}>
      {displayedSegments.map((segment, index) => (
        <span key={index} className={segment.className}>
          {segment.text}
        </span>
      ))}
      <span
        className={cn(
          "ml-1 inline-block h-[1em] w-[2px] bg-white align-baseline animate-pulse",
          cursorClassName,
          currentSegmentIndex >= segments.length && "hidden",
        )}
      />
    </span>
  );
}
